/**
 * Logger Service - Centralized logging with levels and production filtering
 * Provides proper logging levels (DEBUG, INFO, WARN, ERROR) for better production control
 */

const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

class Logger {
  constructor() {
    // Set log level based on environment
    // In production, only show WARN and ERROR
    // In development, show all logs
    this.currentLevel = process.env.NODE_ENV === 'production'
      ? LogLevel.WARN
      : LogLevel.DEBUG;
  }

  /**
   * Set the minimum log level to display
   */
  setLevel(level) {
    this.currentLevel = level;
  }

  /**
   * Format log message with timestamp and context
   */
  formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString();
    const contextStr = Object.keys(context).length > 0
      ? JSON.stringify(context, null, 2)
      : '';

    return {
      timestamp,
      level,
      message,
      context
    };
  }

  /**
   * Debug level - detailed debugging information
   */
  debug(message, context = {}) {
    if (this.currentLevel <= LogLevel.DEBUG) {
      const formatted = this.formatMessage('DEBUG', message, context);
      console.log(`[DEBUG] ${formatted.timestamp}:`, message, context);
    }
  }

  /**
   * Info level - general informational messages
   */
  info(message, context = {}) {
    if (this.currentLevel <= LogLevel.INFO) {
      const formatted = this.formatMessage('INFO', message, context);
      console.log(`[INFO] ${formatted.timestamp}:`, message, context);
    }
  }

  /**
   * Warn level - warning messages
   */
  warn(message, context = {}) {
    if (this.currentLevel <= LogLevel.WARN) {
      const formatted = this.formatMessage('WARN', message, context);
      console.warn(`[WARN] ${formatted.timestamp}:`, message, context);
    }
  }

  /**
   * Error level - error messages
   */
  error(message, context = {}) {
    if (this.currentLevel <= LogLevel.ERROR) {
      const formatted = this.formatMessage('ERROR', message, context);
      console.error(`[ERROR] ${formatted.timestamp}:`, message, context);
    }
  }

  /**
   * Track errors for monitoring (can be extended to send to monitoring service)
   */
  trackError(message, error, additionalContext = {}) {
    const context = {
      ...additionalContext,
      errorMessage: error.message,
      errorStack: error.stack
    };

    this.error(message, context);

    // errorTrackingService.capture(error, context);
  }
}

// Export singleton instance
export const logger = new Logger();
export { LogLevel };
export default logger;
